"use client"

import { useEffect, useState } from "react"
import type { BooruPost } from "@/lib/booru-api"
import { useTagTaxonomyStore } from "@/lib/tag-taxonomy-store"
import { normalizeSuggestionKey, toTaxonomyCategory, type BooruTagCategory } from "@/lib/booru-tag-suggestions"
import { isTagTypeSite } from "@/lib/tag-type-lookup"

// Moved out of components/booru-detail-modal.tsx (same cache, state and effects).

type Rule34DetailsCacheEntry = {
  tags?: string[]
  tagCategories?: Record<string, BooruTagCategory>
  cachedAt: number
}

const RULE34_DETAILS_CACHE_TTL = 24 * 60 * 60 * 1000
const rule34DetailsCache = new Map<string, Rule34DetailsCacheEntry>()

function hasSpecificRule34Categories(categories?: Record<string, BooruTagCategory>): boolean {
  return !!categories && Object.values(categories).some((category) => category !== "general")
}

export function usePostTagDetails({ open, post }: { open: boolean; post: BooruPost | null }) {
  const [rule34DetailTags, setRule34DetailTags] = useState<string[] | null>(null)
  const [rule34DetailCategories, setRule34DetailCategories] = useState<Record<string, BooruTagCategory> | null>(null)

  /** The modal calls this when it moves to another post. */
  const resetTagDetails = () => {
    setRule34DetailTags(null)
    setRule34DetailCategories(null)
  }

  useEffect(() => {
    if (!open || post?.site !== "rule34" || !post?.id) return
    if (hasSpecificRule34Categories(post.tagCategories)) return

    const cacheKey = `rule34:${post.id}`
    const cached = rule34DetailsCache.get(cacheKey)
    if (cached && Date.now() - cached.cachedAt < RULE34_DETAILS_CACHE_TTL) {
      setRule34DetailTags(cached.tags || null)
      setRule34DetailCategories(cached.tagCategories || null)
      return
    }

    let cancelled = false

    fetch(`/api/booru/rule34-post-details?postId=${encodeURIComponent(post.id)}`)
      .then(async (response) => {
        if (!response.ok) return null
        return response.json()
      })
      .then((details: { tags?: string[]; tagCategories?: Record<string, BooruTagCategory> } | null) => {
        if (cancelled || !details) return

        const tags = Array.isArray(details.tags) ? details.tags : undefined
        const tagCategories = details.tagCategories
        const entry: Rule34DetailsCacheEntry = {
          tags,
          tagCategories,
          cachedAt: Date.now(),
        }

        rule34DetailsCache.set(cacheKey, entry)
        setRule34DetailTags(tags || null)
        setRule34DetailCategories(tagCategories || null)

        if (tagCategories) {
          useTagTaxonomyStore.getState().bulkCategorize(
            Object.entries(tagCategories).map(([tag, category]) => ({
              tag: normalizeSuggestionKey(tag),
              category: toTaxonomyCategory(category),
            })),
          )
        }
      })
      .catch((error) => {
        console.warn("[BooruDetailModal] Failed to load Rule34 post tag categories:", error)
      })

    return () => {
      cancelled = true
    }
  }, [open, post?.id, post?.site, post?.tagCategories])

  // Safebooru and Yande.re send no tag types with their posts, so the types are asked for when a post is opened (lib/tag-type-lookup.ts
  // behind /api/booru/tag-types) and fill the same groups Rule34 gets above. A failed lookup leaves every tag as General.
  useEffect(() => {
    if (!open || !post?.id || !(isTagTypeSite(post.site) || post.site === "rule34us") || !post.tags?.length) return
    if (hasSpecificRule34Categories(post.tagCategories)) return

    const cacheKey = `${post.site}:${post.id}`
    const cached = rule34DetailsCache.get(cacheKey)
    if (cached && Date.now() - cached.cachedAt < RULE34_DETAILS_CACHE_TTL) {
      setRule34DetailCategories(cached.tagCategories || null)
      return
    }

    let cancelled = false
    const wanted = post.tags.length
    // The route asks the site for only so many new tags per call, so a post with many tags is asked again (up to 4 times in all) until
    // every tag has an answer; the groups fill in as each answer arrives.
    ;(async () => {
      for (let attempt = 0; attempt < 4 && !cancelled; attempt++) {
        const response = await fetch(post.site === "rule34us" ? `/api/booru/tag-types?site=rule34us&post=${encodeURIComponent(post.id)}` : `/api/booru/tag-types?site=${post.site}&tags=${encodeURIComponent(post.tags.join(","))}`)
        const details: { tagCategories?: Record<string, BooruTagCategory> } | null = response.ok ? await response.json() : null
        const tagCategories = details?.tagCategories
        if (cancelled || !tagCategories || Object.keys(tagCategories).length === 0) return
        rule34DetailsCache.set(cacheKey, { tagCategories, cachedAt: Date.now() })
        setRule34DetailCategories(tagCategories)
        useTagTaxonomyStore.getState().bulkCategorize(
          Object.entries(tagCategories)
            .filter(([, category]) => category !== "general")
            .map(([tag, category]) => ({ tag: normalizeSuggestionKey(tag), category: toTaxonomyCategory(category) })),
        )
        if (post.site === "rule34us" || Object.keys(tagCategories).length >= Math.min(wanted, 120)) return // Rule34.us answers the whole post at once
      }
    })().catch((error) => {
      console.warn("[BooruDetailModal] Failed to load tag types:", error)
    })

    return () => {
      cancelled = true
    }
  }, [open, post?.id, post?.site, post?.tags, post?.tagCategories])

  // A post that arrives with its tag types (Danbooru sends them) writes them into the local tag memory, so the pills
  // are coloured by the real type, the same as the groups above them. "general" is skipped so a type the user set
  // themselves is never overwritten.
  useEffect(() => {
    if (!open || !post?.tagCategories || post.site === "rule34") return
    const typed = Object.entries(post.tagCategories)
      .filter(([, category]) => category !== "general")
      .map(([tag, category]) => ({ tag: normalizeSuggestionKey(tag), category: toTaxonomyCategory(category) }))
    if (typed.length > 0) useTagTaxonomyStore.getState().bulkCategorize(typed)
  }, [open, post?.id, post?.site, post?.tagCategories])

  return { rule34DetailTags, rule34DetailCategories, resetTagDetails }
}
